import React, { useState, useRef, useEffect } from 'react';
import { callGeminiAPI } from '../utils/geminiAPI';
import { defaultAvatarDataUrl } from '../utils/defaultAvatar';

export const ChatModal = ({ isOpen, onClose, profileData, profileOwnerId, user }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  // Internal function that does the actual sending
  const sendMessage = async (textToSend) => {
    const trimmedText = String(textToSend).trim();
    if (!trimmedText || isLoading) return;

    // Convert current messages to format expected by Gemini API
    // ChatModal uses: {role: 'user'|'assistant', content: string}
    // Gemini API expects: {role: 'user'|'ai', text: string}
    const currentMessages = messages.map(msg => ({
      role: msg.role === 'assistant' ? 'ai' : 'user',
      text: msg.content
    }));

    // Add the new user message
    const userMessage = { role: 'user', content: trimmedText };
    const updatedMessages = [...messages, userMessage];
    const updatedContext = [...currentMessages, { role: 'user', text: trimmedText }];

    // Use functional update to avoid stale closure issues
    setMessages(updatedMessages);
    setIsLoading(true);

    try {
      // Call Gemini API with conversation history
      const response = await callGeminiAPI(updatedContext, profileData || {}, trimmedText);
      
      // Validate response
      const responseText = response && typeof response === 'string' ? response.trim() : "I'm having trouble processing that right now. Could you try again?";
      
      setMessages([...updatedMessages, { role: 'assistant', content: responseText }]);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages([...updatedMessages, { role: 'assistant', content: "Connection error. Please try again." }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async (contentOverride) => {
    const textToSend = contentOverride || inputValue;
    if (!textToSend.trim() || isLoading) return;
    setInputValue(''); // Clear input immediately
    await sendMessage(textToSend);
  };

  // Reset messages when modal closes
  useEffect(() => {
    if (!isOpen) {
      setMessages([]);
      setInputValue('');
      setIsLoading(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const showIntro = messages.length === 0 && !isLoading;
  const profileName = profileData?.name || 'Zane Sterling';
  const profileAvatar = profileData?.avatar || defaultAvatarDataUrl(profileData?.name || 'Zane Sterling', 64);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={onClose}
      />
      
      {/* Modal Container */}
      <div className="relative w-full max-w-3xl bg-[#0f111a] rounded-[40px] flex flex-col h-[85vh] shadow-[0_40px_100px_rgba(0,0,0,0.8)] overflow-hidden border border-white/5">
        
        {/* Header */}
        <div className="shrink-0 bg-[#1a1c26] px-8 py-5 flex items-center justify-between border-b border-white/5">
          <div className="flex items-center gap-4">
            <div className="relative">
              <img 
                src={profileAvatar} 
                alt="Avatar" 
                className="w-10 h-10 rounded-full object-cover grayscale brightness-125"
              />
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-[#1a1c26] rounded-full"></div>
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <span className="font-bold text-white text-sm">{profileName}</span>
                <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">AI</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-400">
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" />
                </svg>
                <span className="text-[10px] font-medium tracking-tight">Always Active</span>
              </div>
            </div>
          </div>
          
          <button 
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-[#f43f5e] flex items-center justify-center text-white hover:scale-110 transition-transform shadow-lg"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content Area */}
        <div 
          ref={scrollRef}
          className="flex-1 overflow-y-auto relative bg-gradient-to-b from-[#1a1c2c] to-[#0d0f1a] scrollbar-hide"
        >
          {showIntro ? (
            <div className="h-full flex flex-col items-center justify-center px-8 text-center animate-in fade-in zoom-in duration-700">
              <div className="relative mb-8">
                <div className="absolute inset-0 bg-blue-500 rounded-full blur-3xl opacity-20"></div>
                <img 
                  src={profileAvatar} 
                  alt={profileName} 
                  className="w-32 h-32 rounded-full border-4 border-[#2a2c3c] object-cover relative z-10"
                />
              </div>
              
              <h2 className="text-2xl font-bold mb-8">
                <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  Digital {profileName.split(' ')[0]} • Professional Twin 👥
                </span>
              </h2>
              
              <div className="flex flex-wrap justify-center gap-3">
                {[
                  { label: 'Tech', emoji: '💻', q: "Tell me about your tech stack." },
                  { label: 'Strategy', emoji: '📈', q: "What's your strategy for scaling?" },
                  { label: 'Neural Art', emoji: '🎨', q: "What is neural art?" }
                ].map((topic) => (
                  <button 
                    key={topic.label}
                    onClick={() => handleSend(topic.q)}
                    className="px-5 py-2.5 bg-[#1e2030] hover:bg-[#2a2c3c] border border-white/5 rounded-full flex items-center gap-2.5 text-sm font-semibold text-white transition-all hover:scale-105 active:scale-95"
                  >
                    <span>{topic.emoji}</span>
                    <span>{topic.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="p-8 space-y-6">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] px-5 py-3.5 rounded-2xl text-sm leading-relaxed ${
                    msg.role === 'user' 
                      ? 'bg-[#2a2c3c] text-white border border-white/5 rounded-tr-none' 
                      : 'bg-white/5 text-gray-200 border border-white/5 rounded-tl-none'
                  }`}>
                    {msg.content}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white/5 px-5 py-3.5 rounded-2xl rounded-tl-none border border-white/5">
                    <div className="flex gap-1">
                      <div className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce"></div>
                      <div className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce [animation-delay:-.3s]"></div>
                      <div className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce [animation-delay:-.5s]"></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Input area */}
        <div className="shrink-0 p-8 bg-[#0d0f1a] border-t border-white/5">
          <div className="relative group">
            <input 
              autoFocus
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Type a message..."
              className="w-full bg-white/5 border border-white/10 focus:border-blue-500/50 outline-none rounded-full py-5 px-8 pr-16 text-white text-sm transition-all group-hover:bg-white/[0.08]"
            />
            <button 
              onClick={() => handleSend()}
              disabled={isLoading || !inputValue.trim()}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 bg-gradient-to-br from-[#4f75ff] to-[#7f5eff] rounded-full flex items-center justify-center text-white shadow-lg hover:scale-110 active:scale-95 transition-all disabled:opacity-30"
            >
              <svg className="w-5 h-5 -rotate-45 relative left-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
          
          <div className="mt-5 text-center">
            <span className="text-[10px] font-bold text-gray-600 uppercase tracking-[0.2em]">Powered by PerAI</span>
          </div>
        </div>

      </div>
    </div>
  );
};
